import React, { useContext } from 'react'

import LiveTooltip from 'live/components/ui/tooltip'
import { DashboardContext } from 'live/context/dashboard'

import { useBowtieLanguage } from '../../../../../hooks'
import { hazardBoxHeight, hazardTextAreaMargin, hazardBoxWidth, topEventRadius } from '../../../../../utils/constants'
import { TextContainer, TopBarStyledContainer } from './styles'

interface Props {
    threatsX: number
    consequencesX: number
    relativeMiddleX: number
    relativeMiddleY: number
    scale: number
    hazard: string
    widgetWidth: number
    bowtieCode?: string
}
const TopBar: React.FC<Props> = props => {
    const { threatsX, consequencesX, relativeMiddleX, scale, widgetWidth, hazard, bowtieCode } = props
    const i18n = useBowtieLanguage()
    const { theme: dashTheme } = useContext(DashboardContext)
    // constant to use on the topbar elements width to make for the gap of each element.
    const contextGap = 80

    const relativeTopEventRadius = topEventRadius * scale
    const threatsWidth = relativeMiddleX - relativeTopEventRadius + (threatsX + relativeMiddleX + contextGap) * scale
    const preventionWidth = -(threatsX + relativeMiddleX + contextGap) * scale
    const mitigationWidth = (consequencesX - relativeMiddleX - contextGap) * scale
    const consequenceWidth = widgetWidth - (relativeMiddleX - (relativeMiddleX - consequencesX + contextGap) * scale)

    const drawHazardBackground = (
        <>
            <rect x="0.953125" y="0.643188" width="371.73" height="235.731" fill="black" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.953125 0.643188H16.0641L46.286 236.374H31.1751L0.953125 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M121.841 0.643188H136.952L167.174 236.374H152.063L121.841 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M242.729 0.643188H257.839L288.061 236.374H272.95L242.729 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M61.397 0.643188H76.5079L106.73 236.374H91.6189L61.397 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M182.285 0.643188H197.396L227.618 236.374H212.507L182.285 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M303.172 0.643188H318.283L348.505 236.374H333.394L303.172 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M31.175 0.643188H46.286L76.5079 236.374H61.397L31.175 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M152.063 0.643188H167.174L197.396 236.374H182.285L152.063 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M272.951 0.643188H288.062L318.284 236.374H303.173L272.951 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M91.6189 0.643188H106.73L136.952 236.374H121.841L91.6189 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M212.507 0.643188H227.618L257.839 236.374H242.729L212.507 0.643188Z"
                fill="#FFE600"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M333.394 0.643188H348.505C348.505 0.643188 372.683 187.631 372.683 203.317C372.683 219.002 372.683 236.374 372.683 236.374H363.616L333.394 0.643188Z"
                fill="#FFE600"
            />
        </>
    )

    const drawHazard = (
        <g id="hazard" transform={`translate(${relativeTopEventRadius}, ${40}) scale(${scale})`}>
            <g transform={`translate(${-(hazardBoxWidth / 2)}, ${0})`}>
                <svg
                    width={hazardBoxWidth}
                    height={hazardBoxHeight}
                    viewBox={`0 0 ${hazardBoxWidth} ${hazardBoxHeight}`}
                    fill="none"
                >
                    {drawHazardBackground}
                </svg>

                <rect
                    x={hazardTextAreaMargin}
                    y={hazardTextAreaMargin}
                    fill="#FFFFFF"
                    width={hazardBoxWidth - hazardTextAreaMargin * 2}
                    height={hazardBoxHeight - hazardTextAreaMargin * 2}
                />
                <foreignObject
                    x={hazardTextAreaMargin}
                    y={hazardTextAreaMargin}
                    width={hazardBoxWidth - hazardTextAreaMargin * 2}
                    height={30}
                    style={{ overflow: 'hidden' }}
                >
                    <TextContainer className="bowtie-code">{bowtieCode ?? 'bowtieCode'}</TextContainer>
                </foreignObject>
                <foreignObject
                    x={hazardTextAreaMargin}
                    y={hazardTextAreaMargin + 30}
                    width={hazardBoxWidth - hazardTextAreaMargin * 2}
                    height={hazardBoxHeight - hazardTextAreaMargin * 2 - 30}
                    style={{ overflow: 'hidden' }}
                >
                    <LiveTooltip content={hazard ?? 'Hazard'} position="right">
                        <TextContainer>{hazard ?? 'Hazard'}</TextContainer>
                    </LiveTooltip>
                </foreignObject>
            </g>
        </g>
    )

    return (
        <TopBarStyledContainer id="bowtie-topbar">
            {/* Threats */}
            <g>
                <rect
                    fill={dashTheme === 'light' ? '#def8f8' : '#428fd2'}
                    width={threatsWidth < 0 ? 0 : threatsWidth}
                    height={20}
                />
                <text
                    x={(threatsWidth < 0 ? 0 : threatsWidth) / 2}
                    y={10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    opacity={threatsWidth < 92 ? 0 : 1}
                    fontWeight={600}
                >
                    {i18n('THREAT')}
                </text>
            </g>

            {/* Prevention */}
            <g transform={`translate(${threatsWidth}, ${0})`}>
                <rect
                    fill={dashTheme === 'light' ? '#e0f8de' : '#4ea846'}
                    width={preventionWidth < 0 ? 0 : preventionWidth}
                    height={20}
                />
                <text
                    x={(preventionWidth < 0 ? 0 : preventionWidth) / 2}
                    y={10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    opacity={preventionWidth < 100 ? 0 : 1}
                    fontWeight={600}
                >
                    {i18n('PREVENTION')}
                </text>
            </g>

            {/* Mitigation */}
            <g transform={`translate(${relativeMiddleX + relativeTopEventRadius}, ${0})`}>
                <rect
                    fill={dashTheme === 'light' ? '#f9eccc' : '#f89300'}
                    width={mitigationWidth < 0 ? 0 : mitigationWidth}
                    height={20}
                />
                <text
                    x={(mitigationWidth < 0 ? 0 : mitigationWidth) / 2}
                    y={10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    opacity={mitigationWidth < 100 ? 0 : 1}
                    fontWeight={600}
                >
                    {i18n('MITIGATION')}
                </text>
            </g>
            {/* Consequence */}
            <g transform={`translate(${relativeMiddleX + relativeTopEventRadius + mitigationWidth}, ${0})`}>
                <rect
                    fill={dashTheme === 'light' ? '#DCCEF3' : '#ba90ff'}
                    width={consequenceWidth < 0 ? 0 : consequenceWidth}
                    height={20}
                />

                <text
                    x={(consequenceWidth < 0 ? 0 : consequenceWidth) / 2}
                    y={10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    opacity={consequenceWidth < 92 ? 0 : 1}
                    fontWeight={600}
                >
                    {i18n('CONSEQUENCE')}
                </text>
            </g>

            <g transform={`translate(${relativeMiddleX - relativeTopEventRadius}, ${0})`}>
                <rect
                    className="TopBar-bar"
                    fill={dashTheme === 'light' ? '#f9f9cc' : '#dac700'}
                    width={relativeTopEventRadius * 2}
                    height={25}
                />
                <text
                    x={relativeTopEventRadius}
                    y={12.5}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    opacity={relativeTopEventRadius * 2 < 50 ? 0 : 1}
                    fontWeight={600}
                >
                    {i18n('HAZARD')}
                </text>

                {drawHazard}
            </g>
        </TopBarStyledContainer>
    )
}

export default TopBar
